import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory, useRouteMatch } from 'react-router-dom'
import { IconButton, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ReactFlow, ReactFlowProvider, Background, Controls } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dayjs from 'dayjs'
import AdminPage from '../../../admin/shared/AdminPage'
import Loading from '../../../../molecules/Loading'
import EmptySection from '../../../../atoms/EmptySection'
import SydButton from '../../../../commonDesign/Button'
import SydTabs from '../../../../commonDesign/SydTabs'
import FlexRow from '../../../../molecules/FlexRow'
import FlexActions from '../../../../molecules/FlexActions'
import Icon from '../../../../atoms/Icon'
import OperationalTable from '../../../../organisms/OperationalTable'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import { useGetDag, useListDagExecutions, useRunDag, useUpdateDag, useDeleteDag } from '../../../../../api/dags'
import RunDagDialog from './RunDagDialog'
import DeleteDagDialog from './DeleteDagDialog'
import StepEditorSidebar from './StepEditorSidebar'
import StepListView from './StepListView'
import SettingsTab from './SettingsTab'
import WorkflowStatus from './WorkflowStatus'
import EditableWorkflowCanvas from './EditableWorkflowCanvas'
import { useDagEditor } from './state/useDagEditor'
import { dagToGraph } from './util/dagToGraph'
import { autoLayout } from './util/autoLayout'
import StepNode from './nodes/StepNode'
import { withStepDefaults } from './nodes/withStepDefaults'
import WorkflowEdge from './edges/WorkflowEdge'

const nodeTypes = { step: withStepDefaults(StepNode) }
const edgeTypes = { workflow: WorkflowEdge }

const DETAIL_TABS = {
  steps: { label: 'Steps', value: 'steps' },
  designer: { label: 'Designer', value: 'designer' },
  executions: { label: 'Executions', value: 'executions' },
  settings: { label: 'Settings', value: 'settings' }
}

const useStyles = makeStyles({
  canvasContainer: {
    height: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FAFAFA'
  },
  designerLayout: {
    display: 'flex',
    height: 'calc(100vh - 240px)',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  canvasPane: {
    flex: 1,
    minWidth: 0
  },
  sidePanel: {
    width: 360,
    borderLeft: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    overflow: 'auto',
    flexShrink: 0
  },
  sidePanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 0',
    margin: 0
  },
  sidebarTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600
  },
  sidebarBody: {
    padding: '12px 16px 16px'
  },
  field: {
    marginBottom: 10
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#757575',
    textTransform: 'uppercase',
    marginBottom: 2
  },
  fieldValue: {
    fontSize: 13,
    wordBreak: 'break-all'
  },
  configBlock: {
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
    padding: 8,
    fontSize: 12,
    fontFamily: 'monospace',
    maxHeight: 200,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  },
  headerInput: {
    fontSize: 20,
    fontWeight: 600,
    padding: '4px 0'
  },
  dirtyBadge: {
    fontSize: 11,
    color: '#E65100',
    backgroundColor: '#FFF3E0',
    borderRadius: 4,
    padding: '2px 8px',
    fontWeight: 600,
    marginLeft: 8
  },
  repairBanner: {
    backgroundColor: 'var(--color-warning-050)',
    border: 'var(--border-warning-thin)',
    borderRadius: 'var(--border-radius-sm)',
    padding: 'var(--dim-3) var(--dim-4)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-warning-900)',
    marginBottom: 'var(--dim-2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
})

function StepDetailField ({ label, value }) {
  const classes = useStyles()
  if (value === null || value === undefined) return null
  return (
    <div className={classes.field}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldValue}>{String(value)}</div>
    </div>
  )
}

StepDetailField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any
}

function StepDetailSidebar ({ step, onClose }) {
  const classes = useStyles()

  return (
    <div>
      <div className={classes.sidePanelHeader}>
        <h4 className={classes.sidebarTitle}>{step.name}</h4>
        <IconButton size='small' onClick={onClose} aria-label='close'>
          <Icon name='close' customSize='1rem' />
        </IconButton>
      </div>
      <div className={classes.sidebarBody}>
        <StepDetailField label='Step ID' value={step.dagStepId} />
        <StepDetailField label='Action' value={step.targetAction} />
        <StepDetailField label='Target' value={step.target} />
        <StepDetailField label='Target Identifier' value={step.targetIdentifier} />
        <StepDetailField label='Trigger Event' value={step.triggerEventType} />
        <StepDetailField label='Async' value={step.isAsync ? 'Yes' : 'No'} />
        <StepDetailField label='Complete On' value={step.completeOn} />
        <StepDetailField label='Fail On' value={step.failOn} />
        <StepDetailField label='Parent Step' value={step.parentStepId} />
        {step.configuration ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Configuration</div>
            <div className={classes.configBlock}>
              {JSON.stringify(step.configuration, null, 2)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

StepDetailSidebar.propTypes = {
  step: PropTypes.shape({
    name: PropTypes.string,
    dagStepId: PropTypes.string,
    targetAction: PropTypes.string,
    target: PropTypes.string,
    targetIdentifier: PropTypes.string,
    triggerEventType: PropTypes.string,
    isAsync: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    completeOn: PropTypes.string,
    failOn: PropTypes.string,
    parentStepId: PropTypes.string,
    configuration: PropTypes.object
  }),
  onClose: PropTypes.func
}

function ReadOnlyCanvas ({ dag, onNodeSelect }) {
  const classes = useStyles()

  const { nodes, edges } = useMemo(() => {
    const graph = dagToGraph(dag)
    const layoutNodes = autoLayout(graph.nodes, graph.edges)
    return { nodes: layoutNodes, edges: graph.edges }
  }, [dag])

  const handleNodeClick = useCallback((_event, node) => {
    onNodeSelect(node.data)
  }, [onNodeSelect])

  const handlePaneClick = useCallback(() => {
    onNodeSelect(null)
  }, [onNodeSelect])

  return (
    <div className={classes.canvasContainer}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
          minZoom={0.4}
          maxZoom={2}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  )
}

ReadOnlyCanvas.propTypes = {
  dag: PropTypes.object,
  onNodeSelect: PropTypes.func
}

const executionColumns = [
  { id: 'executionId', accessor: 'executionId', Header: 'ID' },
  {
    id: 'startDate',
    accessor: 'startDate',
    Header: 'Started',
    Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014' // eslint-disable-line react/prop-types
  },
  {
    id: 'finishDate',
    accessor: 'finishDate',
    Header: 'Finished',
    Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014' // eslint-disable-line react/prop-types
  },
  {
    id: 'isError',
    accessor: 'isError',
    Header: 'Status',
    Cell: ({ value }) => value ? 'Error' : 'OK' // eslint-disable-line react/prop-types
  }
]

function ExecutionsTab ({ dagId }) {
  const history = useHistory()
  const { url } = useRouteMatch()
  const { data: executionData, isLoading } = useListDagExecutions(dagId)

  const handleExecutionClick = useCallback((row) => {
    const base = url.replace(/\/executions$/, '')
    history.push(`${base}/executions/${row.original.executionId}`)
  }, [history, url])

  if (isLoading) return <Loading />

  if (!executionData?.length) {
    return (
      <EmptySection
        title='No Executions'
        description='This workflow has not been executed yet.'
      />
    )
  }

  return (
    <OperationalTable
      variant='v2'
      columns={executionColumns}
      data={executionData}
      defaultSort={[{ id: 'executionId', desc: true }]}
      onRowClick={handleExecutionClick}
    />
  )
}

ExecutionsTab.propTypes = {
  dagId: PropTypes.string
}

function WorkflowDetail () {
  const { dagId, tab } = useParams()
  const { url } = useRouteMatch()
  const history = useHistory()

  const { data: dag, isLoading: isLoadingDag } = useGetDag(dagId)
  const { mutateAsync: runDag } = useRunDag()
  const { mutateAsync: updateDag, isLoading: isSaving } = useUpdateDag()
  const { mutateAsync: deleteDag } = useDeleteDag()

  const { state: editorState, selectedStep, actions } = useDagEditor(dag)

  const classes = useStyles()
  const [editing, setEditing] = useState(false)
  const [readOnlySelectedId, setReadOnlySelectedId] = useState(null)
  const runDialogRef = useRef(null)
  const deleteDialogRef = useRef(null)

  useEffect(() => {
    if (dag) {
      actions.setDag(dag, false)
    }
  }, [dag]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editorState.dirty) return
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [editorState.dirty])

  const readOnlySelectedStep = useMemo(() => {
    if (!readOnlySelectedId || !dag?.steps) return null
    return dag.steps.find(s => s.dagStepId === readOnlySelectedId) ?? null
  }, [readOnlySelectedId, dag])

  const handleReadOnlyNodeSelect = useCallback((data) => {
    setReadOnlySelectedId(data?.dagStepId ?? null)
  }, [])

  const handleReadOnlyStepSelect = useCallback((id) => {
    setReadOnlySelectedId(id)
  }, [])

  const handleReadOnlyClose = useCallback(() => {
    setReadOnlySelectedId(null)
  }, [])

  const handleTabChange = useCallback((value) => {
    const base = url.replace(/\/(steps|designer|executions|settings)$/, '')
    history.replace(`${base}/${value}`)
  }, [history, url])

  const handleOpenRunDialog = useCallback(() => {
    if (dag) runDialogRef.current?.open(dag)
  }, [dag])

  const handleRunConfirm = useCallback(async (confirmedDag, dagStepId) => {
    await runDag({
      dagId: confirmedDag.dagId,
      dagStepId: dagStepId || 'start',
      initiator: 'ui'
    })
  }, [runDag])

  const needsRepair = dag?.steps?.length > 0 && !dag.steps.some(s => s.dagStepId === 'start')

  const handleRepair = useCallback(() => {
    if (!dag) return
    const root = dag.steps?.find(s => !s.parentStepId)
    if (!root) return

    const oldId = root.dagStepId
    const patchArr = (arr) => Array.isArray(arr) ? arr.map(id => id === oldId ? 'start' : id) : arr
    const repairedSteps = dag.steps.map(s => {
      const step = { ...s }
      if (step.dagStepId === oldId) step.dagStepId = 'start'
      if (step.parentStepId === oldId) step.parentStepId = 'start'
      const wf = step.configuration?.workflow
      if (wf) {
        step.configuration = {
          ...step.configuration,
          workflow: { ...wf, onComplete: patchArr(wf.onComplete), onFail: patchArr(wf.onFail) }
        }
      }
      return step
    })

    actions.setDag({ ...dag, steps: repairedSteps }, false)
    actions.updateDagMeta({})
    setReadOnlySelectedId(null)
    setEditing(true)
  }, [dag, actions])

  const handleEdit = useCallback(() => {
    actions.setDag(dag, false)
    setReadOnlySelectedId(null)
    setEditing(true)
  }, [dag, actions])

  const handleCancel = useCallback(() => {
    actions.setDag(dag, false)
    setEditing(false)
  }, [dag, actions])

  const handleSave = useCallback(async () => {
    const payload = {
      dagId: editorState.dag.dagId,
      name: editorState.dag.name,
      configuration: editorState.dag.configuration,
      delegatedIdentityCode: editorState.dag.delegatedIdentityCode || null,
      steps: editorState.steps.map(s => ({
        dagStepId: s.dagStepId,
        name: s.name,
        targetAction: s.targetAction || 'pass',
        targetIdentifier: s.targetIdentifier || null,
        triggerEventType: s.triggerEventType || null,
        isAsync: !!s.isAsync,
        completeOn: s.completeOn || null,
        failOn: s.failOn || null,
        parentStepId: s.parentStepId || null,
        configuration: s.configuration || {}
      }))
    }

    await updateDag(payload)
    actions.markClean()
    setEditing(false)
  }, [editorState, updateDag, actions])

  const handleDelete = useCallback(async (dagObj) => {
    await deleteDag({ dagId: dagObj.dagId })
    const workflowsUrl = url.replace(/\/[^/]+\/(steps|designer|executions|settings)$/, '')
    history.push(workflowsUrl)
  }, [deleteDag, history, url])

  const handleOpenDeleteDialog = useCallback(() => {
    if (dag) {
      deleteDialogRef.current?.open(dag)
    }
  }, [dag])

  const readOnlyMenuActions = useMemo(() => [
    { key: 'run', label: 'Run', icon: 'play_arrow', onClick: () => handleOpenRunDialog() },
    { key: 'delete', label: 'Delete', icon: 'delete', onClick: () => handleOpenDeleteDialog() }
  ], [handleOpenRunDialog, handleOpenDeleteDialog])

  if (isLoadingDag) {
    return <AdminPage><Loading /></AdminPage>
  }

  if (!dag) {
    return (
      <AdminPage>
        <EmptySection title='Not Found' description='DAG not found.' />
      </AdminPage>
    )
  }

  const readOnlyStepsContent = dag ? (
    <div className={classes.designerLayout}>
      <div className={classes.canvasPane}>
        <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
          <StepListView
            steps={dag.steps || []}
            selectedStepId={readOnlySelectedId}
            onSelectStep={handleReadOnlyStepSelect}
          />
        </div>
      </div>
      {readOnlySelectedStep ? (
        <div className={classes.sidePanel}>
          <StepDetailSidebar
            step={readOnlySelectedStep}
            onClose={handleReadOnlyClose}
          />
        </div>
      ) : null}
    </div>
  ) : null

  const editStepsContent = (
    <div className={classes.designerLayout}>
      <div className={classes.canvasPane}>
        <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
          <StepListView
            steps={editorState.steps}
            selectedStepId={editorState.selectedStepId}
            onSelectStep={actions.selectStep}
            onRemoveStep={actions.removeStep}
            onAddStep={actions.addStep}
          />
        </div>
      </div>
      {selectedStep ? (
        <div className={classes.sidePanel}>
          <StepEditorSidebar
            step={selectedStep}
            steps={editorState.steps}
            isNew={editorState.isNew}
            onUpdate={actions.updateStep}
            onClose={actions.deselectStep}
          />
        </div>
      ) : null}
    </div>
  )

  const stepsContent = editing ? editStepsContent : readOnlyStepsContent

  const readOnlyDesignerContent = dag ? (
    <div className={classes.designerLayout}>
      <div className={classes.canvasPane}>
        <ReadOnlyCanvas dag={dag} onNodeSelect={handleReadOnlyNodeSelect} />
      </div>
      {readOnlySelectedStep ? (
        <div className={classes.sidePanel}>
          <StepDetailSidebar
            step={readOnlySelectedStep}
            onClose={handleReadOnlyClose}
          />
        </div>
      ) : null}
    </div>
  ) : null

  const editDesignerContent = (
    <div className={classes.designerLayout}>
      <div className={classes.canvasPane}>
        <EditableWorkflowCanvas
          steps={editorState.steps}
          dagName={editorState.dag.name}
          selectedStepId={editorState.selectedStepId}
          onSelectStep={actions.selectStep}
          onDeselectStep={actions.deselectStep}
          onConnect={actions.setParent}
          onRemoveStep={actions.removeStep}
          onAddStep={actions.addStep}
        />
      </div>
      {selectedStep ? (
        <div className={classes.sidePanel}>
          <StepEditorSidebar
            step={selectedStep}
            steps={editorState.steps}
            isNew={editorState.isNew}
            onUpdate={actions.updateStep}
            onClose={actions.deselectStep}
          />
        </div>
      ) : null}
    </div>
  )

  const designerContent = editing ? editDesignerContent : readOnlyDesignerContent

  const headerTitle = editing ? (
    <>
      <TextField
        value={editorState.dag.name}
        onChange={(e) => actions.updateDagMeta({ name: e.target.value })}
        placeholder='Workflow Name'
        InputProps={{ className: classes.headerInput, disableUnderline: true }}
        fullWidth
      />
      <div style={{ color: '#757575', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        {editorState.dag.dagId}
        {editorState.dirty ? <span className={classes.dirtyBadge}>Unsaved</span> : null}
      </div>
    </>
  ) : (
    <>
      <h2 style={{ margin: 0 }}>{dag?.name}</h2>
      <div style={{ color: '#757575', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        {dag?.dagId}
        <WorkflowStatus dagId={dag?.dagId} />
      </div>
    </>
  )

  const headerActions = editing ? (
    <FlexActions>
      <SydButton
        size='sm'
        variant='outline'
        onClick={handleCancel}
      >
        Cancel
      </SydButton>
      <SydButton
        size='sm'
        variant='primary'
        icon='save'
        onClick={handleSave}
        disabled={isSaving || !editorState.dag.dagId || !editorState.dag.name}
        processing={isSaving}
      >
        Save
      </SydButton>
    </FlexActions>
  ) : (
    <FlexActions>
      <SydButton
        size='sm'
        variant='outline'
        icon='edit'
        onClick={handleEdit}
      >
        Edit
      </SydButton>
      <SydActionMenu
        id='workflow-actions'
        actions={readOnlyMenuActions}
      />
    </FlexActions>
  )

  return (
    <AdminPage>
      <FlexRow style={{ width: '100%', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          {headerTitle}
        </div>
        {headerActions}
      </FlexRow>

      {needsRepair && !editing ? (
        <div className={classes.repairBanner}>
          <span>This workflow is missing a root &quot;start&quot; step and cannot be run without specifying a step.</span>
          <SydButton size='sm' variant='outline' onClick={handleRepair}>
            Repair
          </SydButton>
        </div>
      ) : null}

      <div style={{ marginBottom: 8 }}>
        <SydTabs.Group
          key={tab}
          tabOptions={DETAIL_TABS}
          defaultValue={tab}
          onChange={handleTabChange}
        >
          <SydTabs.MiniList />
        </SydTabs.Group>
      </div>

      <div>
        {tab === 'steps' ? stepsContent : null}
        {tab === 'designer' ? designerContent : null}
        {tab === 'executions' ? <ExecutionsTab dagId={dagId} /> : null}
        {tab === 'settings' ? (
          <SettingsTab
            dag={dag}
            editing={editing}
            editorState={editorState}
            onUpdateDagMeta={actions.updateDagMeta}
          />
        ) : null}
      </div>

      <RunDagDialog ref={runDialogRef} onConfirm={handleRunConfirm} />
      <DeleteDagDialog ref={deleteDialogRef} onConfirm={handleDelete} />
    </AdminPage>
  )
}

export default WorkflowDetail
